import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import "./particle-hero-backdrop.css";

/**
 * Rising canvas particle field + atmospheric accents (adapted from particle-hero template).
 * Renders behind {@link ParticleHero} copy; CRA + JSX (no Next, no styled-jsx).
 */
export function ParticleHeroBackdrop() {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const [isGoldMode, setIsGoldMode] = useState(false);
  const particlesRef = useRef([]);
  const animationRef = useRef(null);

  const createParticle = useCallback((canvas) => {
    const w = canvas.width;
    const h = canvas.height;
    const particle = {
      x: 0,
      y: 0,
      speed: 0,
      opacity: 1,
      fadeDelay: 0,
      fadeStart: 0,
      fadingOut: false,
      r: 200,
      barH: 1,
      reset() {
        this.x = Math.random() * w;
        this.y = Math.random() * h;
        this.speed = Math.random() / 5 + 0.1;
        this.opacity = 1;
        this.fadeDelay = Math.random() * 600 + 100;
        this.fadeStart = Date.now() + this.fadeDelay;
        this.fadingOut = false;
        this.r = 160 + Math.random() * 95;
        this.barH = Math.random() * 2 + 1;
      },
      update() {
        this.y -= this.speed;
        if (this.y < 0) {
          this.reset();
        }
        if (!this.fadingOut && Date.now() > this.fadeStart) {
          this.fadingOut = true;
        }
        if (this.fadingOut) {
          this.opacity -= 0.008;
          if (this.opacity <= 0) {
            this.reset();
          }
        }
      },
      draw(ctx) {
        ctx.fillStyle = `rgba(${this.r}, 255, 255, ${this.opacity})`;
        ctx.fillRect(this.x, this.y, 0.4, this.barH);
      },
    };

    particle.reset();
    particle.y = Math.random() * h;
    particle.fadeDelay = Math.random() * 600 + 100;
    particle.fadeStart = Date.now() + particle.fadeDelay;
    particle.fadingOut = false;

    return particle;
  }, []);

  const calculateParticleCount = useCallback((canvas) => {
    const n = Math.max(1, canvas.width * canvas.height);
    return Math.floor(n / 6000);
  }, []);

  const initParticles = useCallback(
    (canvas) => {
      const particleCount = calculateParticleCount(canvas);
      particlesRef.current = [];
      for (let i = 0; i < particleCount; i += 1) {
        particlesRef.current.push(createParticle(canvas));
      }
    },
    [calculateParticleCount, createParticle],
  );

  const animate = useCallback((canvas, ctx) => {
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    particlesRef.current.forEach((p) => {
      p.update();
      p.draw(ctx);
    });
    animationRef.current = requestAnimationFrame(() => {
      animate(canvas, ctx);
    });
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return undefined;

    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    const resize = () => {
      const { width, height } = container.getBoundingClientRect();
      const w = Math.max(1, Math.floor(width));
      const h = Math.max(1, Math.floor(height));
      canvas.width = w;
      canvas.height = h;
      initParticles(canvas);
    };

    resize();

    const ro = new ResizeObserver(() => {
      resize();
    });
    ro.observe(container);

    animate(canvas, ctx);

    return () => {
      ro.disconnect();
      if (animationRef.current != null) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [animate, initParticles]);

  const toggleGoldMode = () => {
    setIsGoldMode((v) => !v);
  };

  return (
    <div
      ref={containerRef}
      className={`particle-hero-fui${isGoldMode ? " particle-hero-fui--gold" : ""}`}
    >
      <div
        className="particle-hero-fui__header"
        style={{
          display: "flex",
          width: "100%",
          justifyContent: "center",
          color: "#7dd3fc",
          padding: "2em",
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          margin: "0 auto",
          opacity: 0,
          transform: "translateY(-1em)",
          animation:
            "particle-hero-fui-load 2s ease-in 2s forwards, particle-hero-fui-up 1.4s ease-out 2s forwards",
        }}
      >
        <button
          type="button"
          aria-label={
            isGoldMode ? "Switch to blue accent theme" : "Switch to gold accent theme"
          }
          className="particle-hero-fui__mid-spot"
          onClick={toggleGoldMode}
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            margin: "0 auto",
            width: "1.8em",
            height: "1.8em",
            borderRadius: "50%",
            background: "black",
            border: "none",
            padding: 0,
            boxShadow: isGoldMode
              ? "0 0 1em 0 #d8bd10"
              : "0 0 1.1em 0 #47b0ff",
            cursor: "pointer",
            transition: "box-shadow 1s ease-in-out",
            zIndex: 2,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.boxShadow = isGoldMode
              ? "-0.3em 0.1em 0.2em 0 #47b0ff"
              : "-0.3em 0.1em 0.2em 0 #d8bd10";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.boxShadow = isGoldMode
              ? "0 0 1em 0 #d8bd10"
              : "0 0 1.1em 0 #47b0ff";
          }}
        />

        <div className="particle-hero-fui__spotlight-wrap">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="particle-hero-fui__spotlight-item"
              data-spot={i}
            />
          ))}
        </div>
      </div>

      <canvas ref={canvasRef} className="particle-hero-fui__canvas" id="particleCanvas" />

      <div
        style={{
          pointerEvents: "none",
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          width: "100%",
          height: "42em",
          zIndex: -2,
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            left: 0,
            margin: "auto",
            height: "100%",
            width: "100%",
          }}
        >
          {[6, 11, 16, 24, 29].map((top, i) => (
            <div
              key={i}
              style={{
                position: "absolute",
                top: `${top}em`,
                right: 0,
                left: 0,
                margin: "auto",
                width: "100%",
                height: "1px",
                background:
                  "linear-gradient(90deg, transparent, rgba(100, 200, 255, 0.38), transparent)",
                opacity: 0,
                transform: "scale(0)",
                animation: "particle-hero-fui-accentload 2s ease-out 2.4s forwards",
              }}
            />
          ))}
        </div>
        <div
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            left: 0,
            margin: "auto",
            height: "100%",
            width: "100%",
          }}
        >
          {[24, 34, -24, -34].map((left, i) => (
            <div
              key={i}
              style={{
                position: "absolute",
                top: 0,
                margin: "auto",
                ...(left > 0
                  ? { left: `${left}em`, right: "auto" }
                  : left < 0
                    ? {
                        left: "auto",
                        right: `${Math.abs(left)}em`,
                      }
                    : { left: 0, right: 0 }),
                width: "1px",
                height: "100%",
                background: "rgba(100, 200, 255, 0.35)",
                opacity: 0,
                transform: "scale(0)",
                animation: "particle-hero-fui-accentload 2s ease-out 2s forwards",
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default ParticleHeroBackdrop;
